import {FormField} from '@components/system/FormField/FormField';
import {ListItemText} from '@material-ui/core';
import {getAllHcpcsCodes} from '@shared/services/SmartCoderServices';
import React, {useEffect, useState} from 'react';
import {Controller, UseFormMethods} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import get from 'lodash/get';
import Loader from '@components/framework/loader/Loader';
import {GENERATE_CPT_CODE_TAB_ID} from '@shared/constants/Mapping';

interface IGenerateHcpcsCodeTab {
  useFormMethods: UseFormMethods;
  clinicGroupId: number;
  clinicId: number;
  relatedId: number;
  relatedType: string;
  defaultValues: Record<string, any>;
  clearGeneratedCPTCode?: () => void;
  onTabChange?: (v: string) => void;
}

const GenerateHcpcsCodeTab = (props: IGenerateHcpcsCodeTab): JSX.Element => {
  const {
    useFormMethods,
    clinicGroupId,
    clinicId,
    defaultValues,
    clearGeneratedCPTCode,
    onTabChange
  } = props;
  const {t} = useTranslation();

  const hcpcsCode = useFormMethods.watch('hcpcs.code');
  const {procedure_code_id: procedureId} = useFormMethods.watch([
    'procedure_code_id'
  ]);

  useEffect(() => {
    if (hcpcsCode?.name_id) {
      useFormMethods.setValue('hcpcs.name_id', hcpcsCode?.name_id);
    } else {
      useFormMethods.setValue('hcpcs.name_id', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hcpcsCode]);

  const {enqueueSnackbar} = useSnackbar();

  const fetchAllHcpcsCodesWithMatchingText = async (
    searchText: string
  ): Promise<any[] | undefined> => {
    try {
      if (searchText?.length < 3) return [];
      const params = {
        label: searchText
      };
      const response = await getAllHcpcsCodes({
        clinicGroupId,
        clinicId,
        params
      });

      if (response.data) {
        return response.data;
      }
      return [];
    } catch (err: any) {
      enqueueSnackbar(err?.message, SnackBarConfig('e'));
    }
  };

  const [defaultHcpcsCodeOption, setDefaultHcpcsCodeOption] = useState({
    data: null,
    isLoading: true
  });

  useEffect(() => {
    const fetchHcpcsCode = async ({nameId}: {nameId: string}) => {
      try {
        setDefaultHcpcsCodeOption(p => ({
          ...p,
          isLoading: true
        }));
        const params = {
          name_id: nameId
        };
        const response = await getAllHcpcsCodes({
          clinicGroupId,
          clinicId,
          params
        });

        if (response.data?.[0]) {
          setDefaultHcpcsCodeOption({
            data: response.data?.[0],
            isLoading: false
          });
        } else {
          setDefaultHcpcsCodeOption({
            data: null,
            isLoading: false
          });
        }
      } catch (err: any) {
        enqueueSnackbar(err?.message, SnackBarConfig('e'));
        setDefaultHcpcsCodeOption({
          data: null,
          isLoading: false
        });
      }
    };

    const defaultNameId = get(defaultValues, 'hcpcs.name_id');

    if (defaultNameId) {
      fetchHcpcsCode({nameId: defaultNameId});
    } else {
      setDefaultHcpcsCodeOption({
        data: null,
        isLoading: false
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeTabToProcedure = () => {
    if (onTabChange) {
      onTabChange(GENERATE_CPT_CODE_TAB_ID.PROCEDURE);
    }
  };

  return (
    <div className="procedure-fields-wrapper">
      {defaultHcpcsCodeOption.isLoading && <Loader />}
      {!defaultHcpcsCodeOption.isLoading && !procedureId && (
        <div>
          <p>
            {t(
              'SMART_CODER.GENERATE_CPT_CODES_DIALOG.TABS.SELECT_PROCEDURE_HELPER_TEXT',
              {
                code_label: 'HCPCS'
              }
            )}
            <span className="link-style" onClick={handleChangeTabToProcedure}>
              {t(
                'SMART_CODER.GENERATE_CPT_CODES_DIALOG.TABS.GO_TO_PROCEDURE_TAB_LINK_TEXT'
              )}
            </span>
          </p>
        </div>
      )}
      {!defaultHcpcsCodeOption.isLoading && !!procedureId && (
        <div className="procedure-middle-fields-wrapper">
          <Controller
            name="hcpcs.code"
            control={useFormMethods.control}
            defaultValue={defaultHcpcsCodeOption?.data}
            render={inputProps => {
              return (
                <FormField
                  type="autocomplete"
                  placeholder={t(
                    'SMART_CODER.GENERATE_CPT_CODES_DIALOG.TABS.HCPCS_DRUG_NAME_PLACEHOLDER'
                  )}
                  label={t(
                    'SMART_CODER.GENERATE_CPT_CODES_DIALOG.TABS.HCPCS_DRUG_NAME_LABEL'
                  )}
                  getOptionSelected={(option, value) => {
                    if (value) {
                      return option.name_id === value.name_id;
                    } else {
                      return false;
                    }
                  }}
                  getOptionLabel={option => {
                    return option?.label;
                  }}
                  renderOption={option => (
                    <ListItemText primary={option?.label} />
                  )}
                  fetchOptions={fetchAllHcpcsCodesWithMatchingText}
                  {...inputProps}
                  onChange={(event: any, value: any) => {
                    if (value) {
                      inputProps.onChange(value);
                    } else {
                      inputProps.onChange(null);
                    }
                    if (clearGeneratedCPTCode) {
                      clearGeneratedCPTCode();
                    }
                  }}
                />
              );
            }}
          />
          <Controller
            control={useFormMethods.control}
            name="hcpcs.name_id"
            defaultValue={get(defaultValues, 'hcpcs.name_id')}
            render={() => <input type="hidden" />}
          />
          <Controller
            control={useFormMethods.control}
            name="hcpcs.unit"
            defaultValue={get(defaultValues, 'hcpcs.unit') ?? null}
            render={inputProps => (
              <FormField
                type="number"
                label={t(
                  'SMART_CODER.GENERATE_CPT_CODES_DIALOG.TABS.HCPCS_UNIT_LABEL'
                )}
                placeholder={t(
                  'SMART_CODER.GENERATE_CPT_CODES_DIALOG.TABS.HCPCS_UNIT_PLACEHOLDER'
                )}
                {...inputProps}
                onChange={e => {
                  if (clearGeneratedCPTCode) {
                    clearGeneratedCPTCode();
                  }
                  inputProps.onChange(e);
                }}
              />
            )}
          />
        </div>
      )}
    </div>
  );
};

export default GenerateHcpcsCodeTab;
